import JSZip from 'jszip';
import { useState } from 'react';

import { downloadFilesApi } from '@/integrations/index.api';
import { handleError } from '@/services/handleError';
import { addExtensionIfMissing, base64ToBlob } from '@/services/helpers/files';
import { useAppSelector } from '@/store';
import type { TFileAttached } from '@/types/financing';

const BANKS_GUARANTEES = {
  altyn: 'grnAltyn',
  bcc: 'grnBcc',
  bereke: 'Гарантийное письмо.pdf',
  forte: 'forteCommitmentLetter',
  halyk: 'guaranteeHalyk',
  shinhan: 'guaranteeShinhan',
  eu_bank: ['LNGA', 'THSG'],
  eu_bank_v2: 'guarantee_letter'
};

const useDownloadAllFiles = () => {
  const { selectedBank, attachedFiles } = useAppSelector(
    (state) => state.financingStep
  );

  const [downloadingFiles, setDownloadingFiles] = useState(false);

  const handleDownloadFiles = async () => {
    setDownloadingFiles(true);

    const copiedGuarantees: Record<string, string | string[]> = {
      ...BANKS_GUARANTEES
    };
    delete copiedGuarantees[selectedBank?.bank.name || ''];
    const unnecessaryGuarantees: string[] =
      Object.values(copiedGuarantees).flat();

    try {
      const zip = new JSZip();
      const uniqueFiles =
        attachedFiles.reduce(
          (result: Record<string, TFileAttached> | undefined, file) => {
            if (!result) result = {};
            if (result[file.uuid] || unnecessaryGuarantees.includes(file.type))
              return result;
            result[file.uuid] = file;
            return result;
          },
          {}
        ) || {};

      const downloadPromises = Object.values(uniqueFiles).map(async (file) => {
        const response = await downloadFilesApi.downloadBankFile(file.uuid);
        const blob =
          !('base64' in response) && 'blob' in response
            ? await (response as { blob: () => Promise<Blob> })?.blob?.()
            : base64ToBlob(response?.base64 || '');
        if (blob)
          zip.file(
            addExtensionIfMissing({ name: file.name, url: file.link }),
            blob
          );
      });

      await Promise.all(downloadPromises);

      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedBank?.bank.name || ''}-documents.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      handleError(error, {
        customTitle:
          'Не получилось скачать документы. Пожалуйста, попробуйте еще раз'
      });
    }
    setDownloadingFiles(false);
  };

  return { downloadingFiles, handleDownloadFiles };
};

export default useDownloadAllFiles;
